import Highcharts from 'highcharts';
import { TimelineAxisSide, TimelineYAxisCustomData, TimelineYAxisTitleOptionsCustomData } from '../../shared';
import { getSeriesYValues } from './get-series-y-values';
import {
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis,
    getPanels_OpraQueries_panels_Panel_timeline_Timeline_rightAxis_Axis,
} from '@queries/generated/central-monitoring.generated';

function mapTitle({
    title,
    unit,
}:
    | getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis
    | getPanels_OpraQueries_panels_Panel_timeline_Timeline_rightAxis_Axis): Highcharts.YAxisTitleOptions &
    TimelineYAxisTitleOptionsCustomData {
    const trimmedUnit = (unit ?? '').trim();

    return {
        // What's displayed in as the y-axis title
        text: trimmedUnit.length > 0 ? `${title} (${trimmedUnit})` : title,
        // The separate components of it
        title,
        unit: trimmedUnit,
    };
}

export type GridLineWidthStrategy = (type: 'numeric' | 'date') => number;

type MapYAxisOptions = {
    axis:
        | getPanels_OpraQueries_panels_Panel_timeline_Timeline_leftAxis_Axis
        | getPanels_OpraQueries_panels_Panel_timeline_Timeline_rightAxis_Axis
        | null;
    side: TimelineAxisSide;
    series: Highcharts.SeriesOptionsType[];
    // Strategy for getting `gridLineWidth` value based on axis series type.
    // E.g. always 0 for date series axis. 1 for first numeric series axis and 0 for later numeric series axes
    gridLineWidthStrategy: GridLineWidthStrategy;
};
export function mapYAxis({
    axis,
    side,
    series,
    gridLineWidthStrategy,
}: MapYAxisOptions): Highcharts.YAxisOptions & TimelineYAxisCustomData {
    // If axis data is null, return a defined but (nearly) empty axis object
    // The `lineWidth` property determines whether the y-axis (left/right border) line shows, so it's included.
    if (axis == null) {
        return { lineWidth: 1 };
    }

    const isRightSide = side === 'right';
    // I don't know what to do if both `dateSeries` and `numericSeries` are defined...
    const isDateSeries = axis.dateSeries != null;

    const yValues = series.flatMap(getSeriesYValues);

    // If no y values, use undefined for min/max, which results in no labels
    const minYValue = yValues.length > 0 ? Math.min(...yValues) : undefined;
    const maxYValue = yValues.length > 0 ? Math.max(...yValues) : undefined;

    // Min
    const min = minYValue != null && !isDateSeries ? minYValue : undefined;
    const max = maxYValue != null && !isDateSeries ? maxYValue : undefined;

    return {
        lineWidth: 1,
        offset: 0,
        showEmpty: true,
        labels: {
            enabled: !isDateSeries,
            reserveSpace: !isDateSeries,
        },

        id: side,
        title: mapTitle(axis),
        opposite: isRightSide,
        // Empty category array is required for y-labels to display correctly
        // (and for date charts to not have some content where y = 0 cut off)
        categories: [],
        min,
        max,

        gridLineWidth: gridLineWidthStrategy(isDateSeries ? 'date' : 'numeric'),
    };
}
